import { Controller } from "@hotwired/stimulus"

/**
 * Accessible navbar controller with keyboard navigation
 * - Toggles aria-expanded on hover/click
 * - Supports keyboard navigation (Arrow keys, Escape, Enter)
 * - Closes on Escape key
 * - Focus management within dropdowns
 */
export default class extends Controller {
  connect() {
    this.currentOpenDropdown = null
    this.timers = new Map() // Store timers for each dropdown
    this.setupDropdowns()
    this.setupGlobalListeners()
  }

  disconnect() {
    this.removeGlobalListeners()
    // Clear all timers
    this.timers.forEach(timer => clearTimeout(timer))
    this.timers.clear()
  }

  setupDropdowns() {
    const dropdowns = document.querySelectorAll('.dropdown-v1')

    dropdowns.forEach((item) => {
      const dropdownContent = item.querySelector('.dropdown-content')
      const dropdownBtn = item.querySelector('.dropbtn')
      
      // Skip if no dropdown content (e.g., "About" link)
      if (!dropdownContent) return

      // Mouse events
      item.addEventListener('mouseenter', (e) => {
        this.openDropdown(item, dropdownBtn, dropdownContent)
      })

      item.addEventListener('mouseleave', () => {
        this.closeDropdown(item, dropdownBtn, dropdownContent)
      })

      // Keyboard events on button
      dropdownBtn.addEventListener('keydown', (e) => {
        this.handleButtonKeydown(e, item, dropdownBtn, dropdownContent)
      })

      // Click event for explicit open/close
      dropdownBtn.addEventListener('click', (e) => {
        const isOpen = dropdownBtn.getAttribute('aria-expanded') === 'true'
        
        if (isOpen) {
          // Allow navigation when clicking the already-open dropdown
          this.closeDropdown(item, dropdownBtn, dropdownContent)
        } else {
          // Prevent navigation and open dropdown on first click
          e.preventDefault()
          this.closeAllDropdowns()
          this.openDropdown(item, dropdownBtn, dropdownContent)
        }
      })

      // Arrow key navigation within dropdown
      dropdownContent.addEventListener('keydown', (e) => {
        this.handleDropdownKeydown(e, dropdownContent, dropdownBtn)
      })
    })
  }

  handleDropdownKeydown(e, content, button) {
    const links = Array.from(content.querySelectorAll('a'))
    const currentIndex = links.indexOf(document.activeElement)

    switch(e.key) {
      case 'ArrowDown':
        e.preventDefault()
        if (currentIndex < links.length - 1) {
          links[currentIndex + 1].focus()
        } else {
          links[0].focus() // Loop to first
        }
        break
      
      case 'ArrowUp':
        e.preventDefault()
        if (currentIndex > 0) {
          links[currentIndex - 1].focus()
        } else {
          links[links.length - 1].focus() // Loop to last
        }
        break
      
      case 'Home':
        e.preventDefault()
        links[0].focus()
        break
      
      case 'End':
        e.preventDefault()
        links[links.length - 1].focus()
        break
      
      case 'Escape':
        e.preventDefault()
        const item = button.closest('.dropdown-v1')
        this.closeDropdown(item, button, content)
        button.focus()
        break
      
      case 'Tab':
        // Allow natural tab order but close dropdown when tabbing out
        if (!content.contains(e.target)) {
          const item = button.closest('.dropdown-v1')
          this.closeDropdown(item, button, content)
        }
        break
    }
  }

  openDropdown(item, button, content) {
    // Clear any existing timer for this dropdown
    const existingTimer = this.timers.get(item)
    if (existingTimer) {
      clearTimeout(existingTimer)
    }
    
    this.closeAllDropdowns()
    
    button.setAttribute('aria-expanded', 'true')
    const timer = setTimeout(() => {
      content.classList.add('open')
      this.timers.delete(item) // Remove timer once it's executed
    }, 100)
    
    // Store the timer
    this.timers.set(item, timer)
    this.currentOpenDropdown = { item, button, content }
  }

  closeDropdown(item, button, content) {
    // Clear any pending open timer
    const timer = this.timers.get(item)
    if (timer) {
      clearTimeout(timer)
      this.timers.delete(item)
    }
    
    button.setAttribute('aria-expanded', 'false')
    content.classList.remove('open')
    
    if (this.currentOpenDropdown?.button === button) {
      this.currentOpenDropdown = null
    }
  }

  closeAllDropdowns() {
    const dropdowns = document.querySelectorAll('.dropdown-v1')
    dropdowns.forEach((item) => {
      const btn = item.querySelector('.dropbtn')
      const content = item.querySelector('.dropdown-content')
      
      // Skip if no dropdown content
      if (!content) return
      
      // Clear any pending timer
      const timer = this.timers.get(item)
      if (timer) {
        clearTimeout(timer)
        this.timers.delete(item)
      }
      
      btn.setAttribute('aria-expanded', 'false')
      content.classList.remove('open')
    })
    this.currentOpenDropdown = null
  }

  handleButtonKeydown(e, item, button, content) {
    const isOpen = button.getAttribute('aria-expanded') === 'true'

    switch(e.key) {
      case 'Enter':
      case ' ': // Space
        e.preventDefault()
        if (isOpen) {
          this.closeDropdown(item, button, content)
        } else {
          this.openDropdown(item, button, content)
          // Focus first link in dropdown
          setTimeout(() => {
            const firstLink = content.querySelector('a')
            if (firstLink) firstLink.focus()
          }, 150)
        }
        break
      
      case 'Escape':
        if (isOpen) {
          e.preventDefault()
          this.closeDropdown(item, button, content)
          button.focus()
        }
        break
      
      case 'ArrowDown':
        if (isOpen) {
          e.preventDefault()
          const firstLink = content.querySelector('a')
          if (firstLink) firstLink.focus()
        } else {
          e.preventDefault()
          this.openDropdown(item, button, content)
          setTimeout(() => {
            const firstLink = content.querySelector('a')
            if (firstLink) firstLink.focus()
          }, 150)
        }
        break
      
      case 'ArrowUp':
        if (isOpen) {
          e.preventDefault()
          const links = content.querySelectorAll('a')
          if (links.length > 0) links[links.length - 1].focus()
        }
        break
    }
  }

  setupGlobalListeners() {
    // Close dropdowns on Escape
    this.escapeHandler = (e) => {
      if (e.key === 'Escape' && this.currentOpenDropdown) {
        this.closeDropdown(
          this.currentOpenDropdown.item,
          this.currentOpenDropdown.button,
          this.currentOpenDropdown.content
        )
        this.currentOpenDropdown.button.focus()
      }
    }
    
    // Close dropdowns on outside click
    this.clickOutsideHandler = (e) => {
      if (this.currentOpenDropdown) {
        const item = this.currentOpenDropdown.item
        if (!item.contains(e.target)) {
          this.closeAllDropdowns()
        }
      }
    }

    document.addEventListener('keydown', this.escapeHandler)
    document.addEventListener('click', this.clickOutsideHandler)
  }

  removeGlobalListeners() {
    if (this.escapeHandler) {
      document.removeEventListener('keydown', this.escapeHandler)
    }
    if (this.clickOutsideHandler) {
      document.removeEventListener('click', this.clickOutsideHandler)
    }
  }
}
